import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { useFlightSearchDataContext } from "@/app/lib/context/FlightSearchDataContext";
import { useFlightSelectionPayloadContext } from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { SingleFlightDetailsProvider } from "@/app/lib/context/SingleFlightDetailsContext";
import { useTravellersSelectionContext } from "@/app/lib/context/TravellersSelectionContext";
import { flightAvailabilityQueryOptions } from "@/app/lib/hooks/useFlightAvailability";
import { travellersQueryOptions } from "@/app/lib/hooks/useTravellers";
import { FlightTrackingEvents, rudderAnalytics } from "@/app/lib/rudderstack";
import {
  getAvailableServices,
  getCheapestMandatoryServices,
} from "@/app/lib/utils/additionalServices";
import { getTravellerTypeAtTravelDate } from "@/app/lib/utils/ageValidation";
import { travelerTypeToTypeCode } from "@/app/lib/utils/transformTraveler";
import { AdditionalServicesSelectionDrawer } from "@/app/ui/flight-purchase/AdditionalServicesSelection/AdditionalServicesSelectionDrawer";
import { SelectedAdditionalServicesCard } from "@/app/ui/flight-purchase/AdditionalServicesSelection/SelectedAdditionalServicesCard";
import BottomDrawer from "@/app/ui/flight-purchase/BottomDrawer";
import { TravellerSelectionStatusDrawer } from "@/app/ui/travellers/TravellerSelectionStatusDrawer";
import TravellersList from "@/app/ui/travellers/TravellersList";
import { TravellersSelection } from "@/app/ui/travellers/TravellersSelection";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { type Traveller, type TravellerAgeGroup } from "@/types/types";
import {
  Feature,
  isFeatureDisabled,
  isFeatureEnabled,
} from "@/utils/featureFlags";

export const Route = createFileRoute(
  "/_authenticated/_flights/_flight-offer/flights/offer/$id/step/2",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const t = useTranslations();
  const navigate = Route.useNavigate();
  const { searchData } = useFlightSearchDataContext();
  const { getTravellerServices, setTravellerServices, selectedFlightPayload } =
    useFlightSelectionPayloadContext();
  const { data: singleFlightDetails, isLoading: isAdditionalDataLoading } =
    useQuery(flightAvailabilityQueryOptions(selectedFlightPayload));

  const {
    travellerSlots,
    selectedTravellers,
    selectTraveller,
    getTravellerIndex,
  } = useTravellersSelectionContext();

  const { data: travellers } = useQuery(travellersQueryOptions());
  const [additionalServicesTraveller, setAdditionalServicesTraveller] =
    useState<Traveller | null>(null);
  const isAdditionalServicesDrawerOpen = !!additionalServicesTraveller;

  const getServicesInfo = useCallback(
    (traveller: Traveller) => {
      const availableServices = getAvailableServices({
        traveller,
        travellerIndex: getTravellerIndex(traveller.id),
        optionalServices: singleFlightDetails?.optionalServices,
        mandatoryServices: singleFlightDetails?.mandatoryServices,
      });

      const selectedServices = getTravellerServices(traveller.id);

      const availableMandatoryServiceCount = availableServices.filter(
        (service) => service.mandatory,
      ).length;

      // null if there are no mandatory services to select from, boolean otherwise
      const hasSelectedAllMandatoryServices =
        availableMandatoryServiceCount > 0
          ? selectedServices.filter((service) => service.mandatory).length ===
            availableMandatoryServiceCount
          : null;

      return {
        availableServices,
        selectedServicesCount: selectedServices.length,
        hasSelectedAllMandatoryServices,
      };
    },
    [
      getTravellerIndex,
      getTravellerServices,
      singleFlightDetails?.mandatoryServices,
      singleFlightDetails?.optionalServices,
    ],
  );

  const hasSelectedMandatoryServicesForAllTravellers = useMemo(() => {
    if (isFeatureDisabled(Feature.AdditionalServiceSelection)) {
      return true;
    }

    return selectedTravellers.every((traveller) => {
      const servicesInfo = getServicesInfo(traveller);

      // Either all mandatory services are selected, or there are no mandatory services to select from
      return (
        servicesInfo.hasSelectedAllMandatoryServices === null ||
        servicesInfo.hasSelectedAllMandatoryServices === true
      );
    });
  }, [selectedTravellers, getServicesInfo]);

  const travelDate = new Date(searchData!.dates.departure);

  const [selectedSlot, setSelectedSlot] = useState<{
    ageGroup: TravellerAgeGroup;
    index: number;
  } | null>(null);

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  return (
    <>
      <div className="px-4">
        <TravellersSelection
          onSlotClick={(ageGroup, index) => {
            setSelectedSlot({ ageGroup, index });
          }}
          renderAdditionalServiceControls={(traveller) => {
            if (isFeatureDisabled(Feature.AdditionalServiceSelection)) {
              return null;
            }

            const { availableServices, selectedServicesCount } =
              getServicesInfo(traveller);

            if (availableServices.length === 0) {
              return null;
            }

            return (
              <SelectedAdditionalServicesCard
                servicesSelectedCount={selectedServicesCount}
                onClick={() => {
                  setAdditionalServicesTraveller(traveller);
                }}
              />
            );
          }}
        />

        <SwipeableDrawer
          hasFullHeight
          heightOffset={24}
          open={!!selectedSlot}
          onClose={() => setSelectedSlot(null)}
          backgroundColor="var(--color-surface-background)"
        >
          <DrawerTopBar
            onCloseClick={() => setSelectedSlot(null)}
            title={
              selectedSlot
                ? t("travellers.addFor", {
                    type: t("travellers.travellerTypeN", {
                      type: selectedSlot.ageGroup,
                      number: selectedSlot.index + 1,
                    }),
                  })
                : null
            }
          />
          {selectedSlot ? (
            <TravellersList
              travellers={(travellers || []).filter(
                (traveller) =>
                  travelerTypeToTypeCode(
                    getTravellerTypeAtTravelDate(
                      traveller.birthDate,
                      travelDate,
                    ),
                  ) === selectedSlot.ageGroup,
              )}
              travelDate={travelDate}
              selectedTravellerId={
                travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
              }
              isSelectionDisabled={(traveller) =>
                selectedTravellers.some((t) => t.id === traveller.id)
              }
              onTravellerSelected={(travellerId) => {
                selectTraveller(selectedSlot.index, travellerId);
                setSelectedSlot(null);
              }}
            />
          ) : null}
        </SwipeableDrawer>
      </div>

      {isFeatureEnabled(Feature.AdditionalServiceSelection) &&
      singleFlightDetails &&
      isAdditionalServicesDrawerOpen ? (
        <SingleFlightDetailsProvider data={singleFlightDetails}>
          <AdditionalServicesSelectionDrawer
            open
            onClose={() => setAdditionalServicesTraveller(null)}
            onConfirm={(travellerId, selectedServices) => {
              setTravellerServices(travellerId, selectedServices);
              setAdditionalServicesTraveller(null);
            }}
            initialSelectedServices={getTravellerServices(
              additionalServicesTraveller.id,
            )}
            traveller={additionalServicesTraveller}
            travellerIndex={getTravellerIndex(additionalServicesTraveller.id)}
          />
        </SingleFlightDetailsProvider>
      ) : null}

      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <BottomDrawer
        buttonLabel={t("flights.continue")}
        onButtonClick={() => {
          rudderAnalytics.track(
            FlightTrackingEvents.USER_CONTINUED_ON_TRAVELERS,
            {
              passenger_count: selectedTravellers.length,
            },
          );

          // TEMP SOLUTION
          // preselect the cheapest mandatory service for each traveller
          selectedTravellers.forEach((traveller) => {
            const { availableServices } = getServicesInfo(traveller);

            const selectedServices =
              getCheapestMandatoryServices(availableServices);

            setTravellerServices(traveller.id, selectedServices);
          });

          navigate({ to: "/flights/offer/$id/step/3" });
        }}
        disabled={
          !allTravellersSelected ||
          !hasSelectedMandatoryServicesForAllTravellers ||
          isAdditionalDataLoading
        }
      />
    </>
  );
}
